import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLicense } from '../hooks/useLicense';
import { PREMIUM_PAGES, SALES_EMAIL } from '../utils/license';
import './LicenseGate.css';

function LockIcon({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 018 0v4" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12l5 5 9-10" />
    </svg>
  );
}

// A page-shaped skeleton, so the lock screen sits on top of something that looks like the page it
// replaces instead of on an empty canvas. Plain bars only; nothing real is read or shown.
function PageSkeleton({ title }) {
  return (
    <div className="lg-skel" aria-hidden="true">
      <h1 className="lg-skel__title">{title}</h1>
      <div className="lg-skel__cards">
        {[0, 1, 2].map((i) => (
          <div className="lg-skel__card" key={i}>
            <div className="lg-bar lg-bar--big" />
            <div className="lg-bar" style={{ width: '80%' }} />
            <div className="lg-bar" style={{ width: '55%' }} />
          </div>
        ))}
      </div>
      <div className="lg-skel__table">
        {[0, 1, 2, 3].map((i) => (
          <div className="lg-skel__row" key={i}>
            <div className="lg-bar" />
            <div className="lg-bar" />
            <div className="lg-bar" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Wraps a Premium-only page. Standard tenants see the page's skeleton and a dialog explaining what
// Premium adds; Trial, Premium and open (still loading) tenants see the page itself.
export function PremiumGate({ page, children }) {
  const license = useLicense();
  const navigate = useNavigate();
  const { tenantId } = useParams();
  if (license.features.documentation) return children;

  const info = PREMIUM_PAGES[page] || { title: 'Denne side', points: [] };
  return (
    <div className="lg-stage">
      <PageSkeleton title={info.title} />
      <div className="lg-scrim">
        <div className="lg-dlg" role="dialog" aria-labelledby="lg-h">
          <span className="lg-pill"><LockIcon />Kræver Premium</span>
          <h2 id="lg-h">{info.title} er en del af Premium</h2>
          <ul className="lg-list">
            {info.points.map((p) => (
              <li key={p}><CheckIcon /><span>{p}</span></li>
            ))}
          </ul>
          <div className="lg-actions">
            <a className="btn-primary lg-btn" href={`mailto:${SALES_EMAIL}`}>Kontakt salg</a>
            <button type="button" className="btn-secondary lg-btn" onClick={() => navigate(`/tenant/${tenantId}`)}>Tilbage</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Shown instead of the whole tenant portal once a Trial has run out. The user can still log in; the
// shell behind is greyed out (see .nc-shell--expired) and nothing in it can be clicked.
export function LicenseExpired() {
  const { role, logout } = useAuth();
  const navigate = useNavigate();
  const backPath = role === 'provider' ? '/organizations' : role === 'admin' ? '/tenants' : null;
  return (
    <div className="lg-scrim lg-scrim--page">
      <div className="lg-dlg" role="alertdialog" aria-labelledby="lg-exp-h">
        <span className="lg-pill lg-pill--warn">Prøveperioden er udløbet</span>
        <h2 id="lg-exp-h">Jeres 30 dages prøveperiode er slut</h2>
        <p>Tak fordi I prøvede NordComply. Jeres opsætning og data er ikke slettet, og alt er klar igen, så snart en licens er på plads.</p>
        <p>Kontakt NordComply, eller bed jeres IT-administrator om at vælge Standard eller Premium.</p>
        <div className="lg-actions">
          <a className="btn-primary lg-btn" href={`mailto:${SALES_EMAIL}`}>Kontakt salg</a>
          {backPath ? (
            <button type="button" className="btn-secondary lg-btn" onClick={() => navigate(backPath)}>Tilbage</button>
          ) : (
            <button type="button" className="btn-secondary lg-btn" onClick={logout}>Log ud</button>
          )}
        </div>
      </div>
    </div>
  );
}
